import React, { useEffect, useImperativeHandle, useRef } from 'react';

import { uploadVideoPosterBlob } from 'js/src/data/app/upload-video-poster-operations';
import uuid from 'js/src/libs/uuid';

type Props = {
    videoId?: number;
    sourceCanvas: HTMLCanvasElement | undefined;
    className?: string;
    ref?: React.RefObject<HTMLCanvasElement>;
    onClick?: React.MouseEventHandler<HTMLCanvasElement>;
};

export const getAspectRatioFromLuminaryCanvasPreview = (
    luminaryCanvasPreview: HTMLCanvasElement | undefined,
) => {
    let aspectRatio = 'landscape'; // default to landscape

    if (!luminaryCanvasPreview) {
        return aspectRatio;
    }

    const width = luminaryCanvasPreview.getAttribute('width');
    const height = luminaryCanvasPreview.getAttribute('height');
    if (width && height) {
        if (width === height) {
            aspectRatio = 'square';
        } else if (parseInt(width) < parseInt(height)) {
            aspectRatio = 'vertical';
        }
    }
    return aspectRatio;
};

/**
 * This is a basic component that can be used to insert a canvas preview into the React dom.
 * It does not know if the content on the canvas changed, it only updates if a new canvas
 * is passed in.
 * The original use case for this is to insert canvas previews into the React dom that were created
 * somewhere else (like luminary generating canvas previews that get stored in redux)
 */
export const CanvasPreview: React.FC<Props> = React.memo(
    React.forwardRef(
        (
            { videoId, sourceCanvas, className, onClick },
            canvasRefExternal: React.ForwardedRef<HTMLCanvasElement | null>,
        ) => {
            const canvasRef = useRef<HTMLCanvasElement | null>(null);
            useImperativeHandle<HTMLCanvasElement | null, HTMLCanvasElement | null>(
                canvasRefExternal,
                () => canvasRef.current,
            );

            /**
             * When a new canvas is passed in, we need to update the preview canvas
             */
            useEffect(() => {
                if (!sourceCanvas) {
                    return;
                }
                if (!canvasRef.current) {
                    return;
                }

                canvasRef.current.width = sourceCanvas.width;
                canvasRef.current.height = sourceCanvas.height;
                const ctx = canvasRef.current.getContext('2d');
                if (!ctx) {
                    return;
                }
                ctx.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height);
                if (!videoId) {
                    return;
                }
                const fileName = `${uuid()}.jpg`;
                const fileType = 'image/jpeg';
                const quality = 0.95;
                // if videoId exists we will automatically update the video poster_url
                // Here we are converting the canvas to a blob and uploading it to the server
                sourceCanvas.toBlob(
                    (blob) => uploadVideoPosterBlob(blob, fileName, fileType, videoId),
                    fileType,
                    quality,
                );
            }, [sourceCanvas, videoId]);

            return <canvas ref={canvasRef} className={className} onClick={onClick} />;
        },
    ),
);

CanvasPreview.displayName = 'CanvasPreview';
